import React, { useEffect, useRef, useState } from "react";
import { TbTrash } from "react-icons/tb";
import Ratio from "react-ratio";
import { useLocation } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import styled from "styled-components";
import toast from "../../../components/Toast";
import theme from "../../../styles/colors";
import DeletePopUp from "./DeletePopUp";
import { useCursorPosition } from "./useCursorPosition";

const isBrowser = typeof window !== "undefined";

// Styled components remain the same...
const OuterDiv = styled.div`
  background-image: ${(props) => `url(${props.image})`};
  background-size: cover;
  background-repeat: no-repeat;
  width: 100%;
  height: 100%;
`;
const InnerDiv = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  display: inline;
  object-fit: contain;
  inset: 0;
  border-collapse: separate;
  border-bottom: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;
const StyledDelete = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  background: rgb(${(props) => props.theme.colors.offWhite});
  border: 2px solid rgb(${(props) => props.theme.colors.neutral100});
  border-radius: 100px;
  position: absolute;
  top: 0px;
  right: 0px;
  width: 40px;
  margin: 24px;
  height: 40px;
  z-index: 1;
  cursor: pointer;

  :hover {
    border: 2px solid rgb(${(props) => props.theme.colors.neutral200});
    svg {
      color: rgb(${(props) => props.theme.colors.neutral400});
    }
  }
  svg {
    color: rgb(${(props) => props.theme.colors.neutral200});
  }
`;
const AspectRatio = styled(Ratio)`
  display: block;
  overflow: hidden;
`;
const CaptionContainer = styled.div`
  background: rgb(${(props) => props.theme.colors.lgtGrey});
  padding: 16px 24px;
  /* height: 56px; */
  font-family: "Inter", Arial, Helvetica, sans-serif;
  font-size: 17px;
  font-weight: 500;
  line-height: 19px;
  width: 100%;
  letter-spacing: 0em;
  color: rgb(${(props) => props.theme.colors.neutral600});
  outline: 0;
  border: 0;
  transition: height 200ms ease-in-out;
  resize: none;
  box-sizing: border-box;
  background-color: rgb(${(props) => props.theme.colors.white});
  &[contenteditable="true"]:empty:before {
    content: attr(placeholder);
    color: rgb(${(props) => props.theme.colors.neutral600});
    font-style: italic;
    pointer-events: none;
    display: block; /* For Firefox */
  }

  border-top-right-radius: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  &:active,
  &:focus {
    border: 2px solid rgb(${(props) => props.theme.colors.neutral600});
  }
`;
const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  /* margin: 16px 0px; */

  cursor: grabbing;
  cursor: grab;
  cursor: -moz-grab;
  cursor: -webkit-grab;

  &:active {
    cursor: grabbing;

    cursor: grabbing;
    cursor: -moz-grabbing;
    cursor: -webkit-grabbing;
  }
  border-bottom: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;
const ImageUpload = (props) => {
  const { editor } = props;
  const [open, setOpen] = React.useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const location = useLocation();
  const captionRef = useRef(null);
  const [internalCaption, setInternalCaption] = useState(props.caption);
  const { saveCursorPosition, restoreCursorPosition, cursorPositionRef } =
    useCursorPosition();

  useEffect(() => {
    if (props.caption !== internalCaption) {
      setInternalCaption(props.caption);

      if (captionRef.current && document.activeElement !== captionRef.current) {
        captionRef.current.textContent = props.caption;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.caption]);

  const deleteImage = () => {
    if (!editor) return;

    const { state } = editor;
    const { selection } = state;
    const { from } = selection;

    // Get the selected node directly from document
    const $pos = state.doc.resolve(from);
    const selectedNode = state.doc.nodeAt(from);

    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        if ($pos.parent.type.name === "paragraph") {
          // For images inside paragraphs, we'll replace the entire paragraph
          // content with the filtered content (excluding the selected node)
          const paragraphPos = $pos.before();
          const paragraph = state.doc.nodeAt(paragraphPos);

          if (!paragraph?.content) return false;

          // Get the index of the selected node
          const { index } = paragraph.content.findIndex($pos.parentOffset);

          // Create new content array excluding the selected node
          const newContent = [];
          for (let i = 0; i < paragraph.content.childCount; i++) {
            if (i !== index) {
              newContent.push(paragraph.content.child(i));
            }
          }

          // Replace the entire paragraph with a new one containing filtered content
          const newParagraph = state.schema.nodes.paragraph.create(
            null,
            newContent,
          );

          tr.replaceWith(
            paragraphPos,
            paragraphPos + paragraph.nodeSize,
            newParagraph,
          );
        } else {
          // Case 2: Image directly in doc
          const nodeSize = selectedNode?.nodeSize ?? 0;
          tr.delete(from, from + nodeSize);
        }
        return true;
      })
      .run();
  };

  const handleDelete = () => {
    if (location.pathname.split("/")[2] === "new") {
      deleteImage();
    } else {
      setOpen(true);
    }
  };

  const close = () => {
    setOpen(false);
  };

  useEffect(() => {
    // Bumped when Backspace/Delete is pressed on the selected image node —
    // run the same flow as the trash button (dialog on edit, instant on create).
    if (props.deleteRequest) {
      handleDelete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.deleteRequest]);

  const [loaded, setLoaded] = useState(false);

  const toggleOutline = () => {
    const element = document.querySelector(
      ".node-image.ProseMirror-selectednode",
    );
    if (isBrowser && isFocused) {
      if (element) {
        element.style.outlineColor = "transparent";
      }
    } else {
      if (element) {
        element.style.outlineColor = "rgb(11, 12, 15)";
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData
      .getData("text/plain")
      .trim()
      .split("\n")
      .join(" ");

    if (captionRef.current && isBrowser) {
      const selection = window.getSelection();
      if (!selection) return;

      const range = selection.getRangeAt(0);
      const startOffset = range.startOffset;

      const currentText = captionRef.current.textContent ?? "";

      const newText =
        currentText.slice(0, startOffset) +
        text +
        currentText.slice(startOffset);

      captionRef.current.textContent = newText;

      const newCursorPosition = startOffset + text.length;

      const newRange = document.createRange();
      const textNode = captionRef.current.firstChild || captionRef.current;

      try {
        newRange.setStart(textNode, newCursorPosition);
        newRange.setEnd(textNode, newCursorPosition);

        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        console.error("Error setting cursor position:", e);
      }

      setInternalCaption(newText);
      props.updateCaption(newText);
    }
  };

  useEffect(() => {
    if (captionRef.current && props.caption) {
      captionRef.current.textContent = props.caption;
    }
    return () => {
      cursorPositionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    toggleOutline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalCaption, isFocused]);

  return (
    <>
      <ImageWrapper
        onDrag={(e) => {
          if (e.clientY < 160) {
            window.scrollBy(0, -20);
          }
        }}
        tabIndex={0}
      >
        <AspectRatio ratio={2 / 1}>
          <DeletePopUp open={open} close={close} onDeleteImage={deleteImage} />
          <OuterDiv
            image={props.src}
            data-clarity-mask="True"
            className="ph-no-capture"
          >
            <StyledDelete onClick={handleDelete}>
              <TbTrash size={20} />
            </StyledDelete>

            <div
              style={{
                backgroundColor: "rgba(226, 228, 233, 0.2)",
                backdropFilter: "blur(32px)",
                position: "absolute",
                inset: 0,
                backgroundImage: "inherit",
                backgroundSize: "cover",
                filter: "blur(32px)",
                borderRadius: "8px",
              }}
            />
            {!loaded && (
              <SpinnerCircular
                color={`rgb(${theme.colors.neutral600})`}
                secondaryColor={`rgb(${theme.colors.neutral100})`}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            )}
            <InnerDiv
              src={props.src}
              onLoad={() => setLoaded(true)}
              data-clarity-mask="True"
              className="ph-no-capture"
            />
          </OuterDiv>
        </AspectRatio>
      </ImageWrapper>
      <CaptionContainer
        contentEditable={"true"}
        placeholder="Add your photo caption here (Optional)"
        ref={captionRef}
        data-clarity-mask="True"
        className="ph-no-capture"
        draggable="false"
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onPaste={handlePaste}
        onKeyDown={(e) => {
          if (e.code === "Enter") {
            e.preventDefault();
            toast({
              toastId: "noNewLine",
              message:
                "Line breaks aren't allowed for photo captions—this keeps your book layout looking polished.",
            });
          }
        }}
        onInput={(e) => {
          saveCursorPosition(captionRef.current);
          const value = (e.currentTarget.textContent ?? "").trim();
          if (e.currentTarget.innerHTML === "<br>") {
            e.currentTarget.innerHTML = "";
          }
          setInternalCaption(value);
          props.updateCaption(value);

          setTimeout(() => restoreCursorPosition(captionRef.current), 0);
        }}
        onMouseDown={(e) => {
          setIsFocused(true);

          const dragRoot = e.currentTarget.closest('[draggable="true"]');
          if (dragRoot) {
            dragRoot.draggable = false;
            document.addEventListener(
              "mouseup",
              () => {
                dragRoot.draggable = true;
              },
              { once: true },
            );
          }
        }}
        onFocus={() => {
          setIsFocused(true);
          if (!internalCaption && captionRef.current) {
            const range = document.createRange();
            const selection = window.getSelection();
            if (selection) {
              range.selectNodeContents(captionRef.current);
              range.collapse(false);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
      />
    </>
  );
};

export default ImageUpload;
